import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, TextField, Alert, Typography, MenuItem, Grid,
  FormControlLabel, Switch, Paper, Chip, Divider, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { superAdminAPI } from '../../services/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { SuperAdminDataTable, apiErrorMessage, statusChip, AdminTableEditButton } from '../../components/superadmin/SuperAdminDataTable';
import { SuperAdminDetailDrawer, SuperAdminDetailField } from '../../components/superadmin/SuperAdminDetailDrawer';
import { useLocale } from '../../contexts/LocaleContext';
import { calculateInvoiceAmounts } from '../../utils/billingCalculations';
import { formatMoney } from '../../utils/localeFormat';
import { LOCALE_DEFAULTS } from '../../i18n/localeConfig';
import { RADIUS, brandColors, brandCss } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';

const defaultForm = {
  tenantId: '',
  pricingType: 'plan' as 'subscription' | 'plan' | 'custom',
  planName: '',
  customPlanName: '',
  billingCycle: 'monthly',
  baseAmount: 0,
  isCustomPrice: false,
  gstEnabled: true,
  gstRate: 18,
  discountAmount: 0,
  discountPercent: 0,
  paymentMode: 'pending',
  billingPeriod: '',
  dueDate: '',
  localeLanguage: LOCALE_DEFAULTS.language,
  localeCurrency: LOCALE_DEFAULTS.currency,
  localeTimezone: LOCALE_DEFAULTS.timezone,
  localeDateFormat: LOCALE_DEFAULTS.dateFormat,
};

const PAYMENT_MODES = [
  { value: 'pending', label: 'Pending' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'razorpay', label: 'Razorpay' },
  { value: 'other', label: 'Other' },
];

export const BillingInvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [tenantFilter, setTenantFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [form, setForm] = useState(defaultForm);
  const [checkNumber, setCheckNumber] = useState('');
  const [checkResult, setCheckResult] = useState('');
  const [detailRow, setDetailRow] = useState<any>(null);
  const navigate = useNavigate();
  const { formatCurrency, formatDate, languages, currencies, timezones, dateFormats } = useLocale();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {};
      if (tenantFilter !== 'all') params.tenantId = tenantFilter;
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;

      const [invoiceRes, tenantRes, subRes, planRes] = await Promise.all([
        superAdminAPI.getBillingInvoices(params),
        superAdminAPI.getTenants(),
        superAdminAPI.getSubscriptions(),
        superAdminAPI.getPlans(),
      ]);
      setInvoices(invoiceRes.data);
      setTenants(tenantRes.data);
      setSubscriptions(subRes.data);
      setPlans(planRes.data.filter((p: any) => p.status === 'active'));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load invoices'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantFilter, fromDate, toDate]);

  const tenantOptions = useMemo(
    () => tenants.map((t) => ({ id: t.tenantId, name: t.companyName, ...t })),
    [tenants]
  );

  const selectedTenant = useMemo(
    () => tenantOptions.find((t) => t.id === form.tenantId),
    [tenantOptions, form.tenantId]
  );

  const selectedSub = useMemo(
    () => subscriptions.find((s) => s.tenantId === form.tenantId),
    [subscriptions, form.tenantId]
  );

  const selectedPlan = useMemo(
    () => plans.find((p) => p.planName === form.planName),
    [plans, form.planName]
  );

  const stats = useMemo(() => ({
    total: invoices.length,
    pending: invoices.filter((i) => i.status === 'pending').length,
    paid: invoices.filter((i) => i.status === 'paid').length,
    revenue: invoices.filter((i) => i.status === 'paid').reduce((s, i) => s + (i.totalAmount || 0), 0),
  }), [invoices]);

  const preview = useMemo(
    () => calculateInvoiceAmounts(form.baseAmount, form.gstEnabled, form.gstRate, form.discountAmount, form.discountPercent),
    [form.baseAmount, form.gstEnabled, form.gstRate, form.discountAmount, form.discountPercent]
  );

  const formatPreview = (amount: number) =>
    formatMoney(amount, form.localeCurrency, form.localeLanguage);

  const applyTenantLocale = (tenantId: string) => {
    const tenant = tenantOptions.find((t) => t.id === tenantId);
    if (!tenant) return;
    setForm((f) => ({
      ...f,
      tenantId,
      localeLanguage: tenant.language || LOCALE_DEFAULTS.language,
      localeCurrency: tenant.currency || LOCALE_DEFAULTS.currency,
      localeTimezone: tenant.timeZone || LOCALE_DEFAULTS.timezone,
    }));
  };

  const applyPlanPrice = (planName: string, billingCycle: string, isCustomPrice: boolean) => {
    const plan = plans.find((p) => p.planName === planName);
    if (!plan || isCustomPrice) return;
    const price = billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
    setForm((f) => ({ ...f, planName, billingCycle, baseAmount: price }));
  };

  useEffect(() => {
    if (form.pricingType === 'subscription' && selectedSub) {
      setForm((f) => ({
        ...f,
        planName: selectedSub.planName,
        billingCycle: selectedSub.billingCycle,
        baseAmount: selectedSub.price,
        gstEnabled: selectedSub.gstEnabled,
        gstRate: selectedSub.gstRate,
      }));
    }
  }, [form.pricingType, selectedSub]);

  const handleCreate = async () => {
    try {
      const payload: Record<string, unknown> = {
        ...form,
        autoFromSubscription: form.pricingType === 'subscription',
        planName: form.pricingType === 'custom' ? form.customPlanName : form.planName,
      };
      await superAdminAPI.createBillingInvoice(payload);
      setMessage('Invoice created successfully');
      setOpen(false);
      setForm(defaultForm);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to create invoice'));
    }
  };

  const handleAutoGenerate = async () => {
    try {
      const res = await superAdminAPI.runBillingJob();
      const r = res.data?.result;
      setMessage(`Auto billing done: ${r?.generated ?? 0} invoice(s) generated`);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Auto billing failed'));
    }
  };

  const handleCheckNumber = async () => {
    if (!checkNumber.trim()) return;
    try {
      const res = await superAdminAPI.checkBillingInvoiceNumber(checkNumber.trim());
      if (res.data.exists) {
        setCheckResult(`Invoice #${checkNumber} already exists (${res.data.invoice?.companyName})`);
      } else {
        setCheckResult(`Invoice #${checkNumber} is available`);
      }
    } catch (err) {
      setCheckResult(apiErrorMessage(err, 'Check failed'));
    }
  };

  const canCreate = form.tenantId && (
    form.pricingType === 'subscription'
      ? !!selectedSub
      : form.pricingType === 'custom'
        ? !!form.customPlanName && form.baseAmount > 0
        : !!form.planName && form.baseAmount > 0
  );

  const buildInvoiceFields = (i: any): SuperAdminDetailField[] => [
    { label: 'Invoice No', value: i.invoiceNumber },
    { label: 'Tenant', value: i.companyName },
    { label: 'Plan', value: i.planName },
    { label: 'Billing Cycle', value: i.billingCycle === 'yearly' ? 'Yearly' : 'Monthly' },
    { label: 'Billing Period', value: i.billingPeriod || '—' },
    { label: 'Amount', value: formatMoney(i.totalAmount, i.localeCurrency || 'INR', i.localeLanguage || 'en') },
    { label: 'Base Amount', value: i.baseAmount != null ? formatMoney(i.baseAmount, i.localeCurrency || 'INR', i.localeLanguage || 'en') : '—' },
    { label: 'GST', value: i.gstEnabled ? `${i.gstRate}%` : 'Off' },
    { label: 'Payment Mode', value: i.paymentMode || '—' },
    { label: 'Due Date', value: i.dueDate ? formatDate(i.dueDate) : '—' },
    { label: 'Locale', value: `${i.localeCurrency || 'INR'} · ${i.localeLanguage || 'en'}` },
    { label: 'Timezone', value: i.localeTimezone || '—' },
    { label: 'Created', value: i.createdAt ? formatDate(i.createdAt) : '—' },
    { label: 'Custom Price', value: i.isCustomPrice ? 'Yes' : 'No' },
    { label: 'Invoice ID', value: String(i._id || ''), fullWidth: true },
  ];

  return (
    <Box>
      <PageHeader
        title="Platform Invoices"
        subtitle={getPageSubtitle('billing_invoices')}
        icon={<ReceiptLongIcon />}

        action={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={() => navigate('/super-admin/billing-profile')}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Billing Profile
            </Button>
            <Button variant="outlined" startIcon={<AutorenewIcon />} onClick={handleAutoGenerate}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Auto Generate Due
            </Button>
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => { setForm(defaultForm); setOpen(true); }}
              sx={catchyPrimaryButtonSx}>
              Create Invoice
            </Button>
          </Box>
        }
      />
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Invoices', value: stats.total, color: brandCss.primary },
          { label: 'Pending', value: stats.pending, color: '#f59e0b' },
          { label: 'Paid', value: stats.paid, color: '#1565c0' },
          { label: 'Collected Revenue', value: formatCurrency(stats.revenue), color: '#3b82f6' },
        ].map((s) => (
          <Grid item xs={6} md={3} key={s.label}>
            <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${brandColors.border}` }}>
              <Typography fontSize={11} fontWeight={700} color="text.secondary">{s.label}</Typography>
              <Typography fontWeight={800} fontSize="1.4rem" sx={{ color: s.color }}>{s.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <TextField select size="small" label="Tenant" value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)} sx={{ minWidth: 200, ...catchyTextFieldSx }}>
          <MenuItem value="all">All Tenants</MenuItem>
          {tenantOptions.map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" type="date" label="From" value={fromDate}
          onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }}
          sx={{ ...catchyTextFieldSx, width: 160 }} />
        <TextField size="small" type="date" label="To" value={toDate}
          onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }}
          sx={{ ...catchyTextFieldSx, width: 160 }} />
        <TextField size="small" label="Check invoice no." value={checkNumber}
          onChange={(e) => setCheckNumber(e.target.value)} sx={{ ...catchyTextFieldSx, width: 200 }} />
        <Button size="small" variant="outlined" onClick={handleCheckNumber}>Verify</Button>
        {checkResult && <Typography fontSize={12} color="text.secondary">{checkResult}</Typography>}
      </Box>

      <SuperAdminDataTable
        rows={invoices}
        loading={loading}
        error={error}
        exportFileName="super-admin-platform-invoices"
        exportSheetName="Invoices"
        statusField="status"
        searchPlaceholder="Search invoice no, tenant, plan..."
        statusOptions={[
          { label: 'All Status', value: 'all' },
          { label: 'Pending', value: 'pending' },
          { label: 'Paid', value: 'paid' },
          { label: 'Overdue', value: 'overdue' },
          { label: 'Partially Paid', value: 'partially_paid' },
        ]}
        columns={[
          {
            id: 'invoiceNo',
            header: 'Invoice No',
            searchValue: (i) => i.invoiceNumber,
            exportValue: (i) => i.invoiceNumber,
            render: (i) => <Typography fontWeight={600} fontSize={13}>{i.invoiceNumber}</Typography>,
          },
          {
            id: 'tenant',
            header: 'Tenant',
            searchValue: (i) => i.companyName,
            exportValue: (i) => i.companyName,
            render: (i) => i.companyName,
          },
          {
            id: 'plan',
            header: 'Plan',
            searchValue: (i) => i.planName,
            exportValue: (i) => i.planName,
            render: (i) => (
              <Box>
                <Typography fontSize={13}>{i.planName}</Typography>
                {i.isCustomPrice && <Chip label="Custom price" size="small" sx={{ height: 18, fontSize: 10, mt: 0.25 }} />}
              </Box>
            ),
          },
          {
            id: 'cycle',
            header: 'Cycle',
            exportValue: (i) => i.billingCycle,
            filterType: 'select',
            filterOptions: [{ label: 'Monthly', value: 'monthly' }, { label: 'Yearly', value: 'yearly' }],
            filterValue: (i) => i.billingCycle,
            render: (i) => i.billingCycle === 'yearly' ? 'Yearly' : 'Monthly',
          },
          {
            id: 'locale',
            header: 'Locale',
            exportValue: (i) => `${i.localeCurrency || 'INR'} / ${i.localeLanguage || 'en'}`,
            render: (i) => (
              <Typography fontSize={12}>
                {i.localeCurrency || 'INR'} · {i.localeLanguage || 'en'}
              </Typography>
            ),
          },
          {
            id: 'period',
            header: 'Period',
            searchValue: (i) => i.billingPeriod,
            exportValue: (i) => i.billingPeriod,
            render: (i) => i.billingPeriod,
          },
          {
            id: 'amount',
            header: 'Amount',
            searchValue: (i) => String(i.totalAmount),
            exportValue: (i) => i.totalAmount,
            render: (i) => formatMoney(i.totalAmount, i.localeCurrency || 'INR', i.localeLanguage || 'en'),
          },
          {
            id: 'status',
            header: 'Status',
            exportValue: (i) => i.status,
            filterType: 'select',
            filterOptions: [
              { label: 'Pending', value: 'pending' },
              { label: 'Paid', value: 'paid' },
              { label: 'Overdue', value: 'overdue' },
              { label: 'Partially Paid', value: 'partially_paid' },
            ],
            filterValue: (i) => i.status,
            render: (i) => statusChip(i.status),
          },
          {
            id: 'date',
            header: 'Date',
            searchValue: (i) => i.createdAt ? String(i.createdAt) : '',
            exportValue: (i) => i.createdAt,
            render: (i) => formatDate(i.createdAt),
          },
        ]}
        onRowClick={setDetailRow}
        renderActions={(i) => (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
            <AdminTableEditButton onClick={() => navigate(`/super-admin/invoices/${i._id}`)} title="View / Edit invoice" />
            <Button size="small" variant="outlined" startIcon={<VisibilityIcon fontSize="small" />}
              onClick={() => navigate(`/super-admin/invoices/${i._id}`)}
              sx={{ borderRadius: `${RADIUS}px`, textTransform: 'none', fontWeight: 600, fontSize: 12 }}>
              View
            </Button>
          </Box>
        )}
      />

      <SuperAdminDetailDrawer
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        moduleTitle="Platform Invoice"
        title={detailRow?.invoiceNumber || ''}
        subtitle={detailRow?.companyName}
        status={detailRow?.status}
        fields={detailRow ? buildInvoiceFields(detailRow) : []}
        primaryActions={detailRow ? (
          <Button variant="contained" startIcon={<VisibilityIcon />}
            onClick={() => navigate(`/super-admin/invoices/${detailRow._id}`)}
            sx={catchyPrimaryButtonSx}>
            View / Edit Invoice
          </Button>
        ) : undefined}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: `${RADIUS}px` } }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={1}>Create Platform Invoice</Typography>
          <Typography fontSize={13} color="text.secondary" mb={2}>
            Choose a subscription plan, pick a dynamic plan, or enter a custom price with locale settings.
          </Typography>

          <Typography fontWeight={700} fontSize={12} color={brandCss.primaryDark} mb={1}>PRICING SOURCE</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={form.pricingType}
            onChange={(_, v) => v && setForm((f) => ({ ...f, pricingType: v }))}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="subscription">From Subscription</ToggleButton>
            <ToggleButton value="plan">Dynamic Plan</ToggleButton>
            <ToggleButton value="custom">Custom Price</ToggleButton>
          </ToggleButtonGroup>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth label="Tenant" value={form.tenantId}
                onChange={(e) => applyTenantLocale(e.target.value)}
                sx={catchyTextFieldSx}>
                {tenantOptions.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {form.pricingType === 'subscription' && (
              <Grid item xs={12}>
                {selectedSub ? (
                  <Alert severity="info" sx={{ fontSize: 13 }}>
                    Subscription: <strong>{selectedSub.planName}</strong> · {selectedSub.billingCycle} · {formatPreview(selectedSub.price)}
                  </Alert>
                ) : (
                  <Alert severity="warning">No active subscription for this tenant. Use Dynamic Plan or Custom Price.</Alert>
                )}
              </Grid>
            )}

            {form.pricingType === 'plan' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label="Plan" value={form.planName}
                    onChange={(e) => applyPlanPrice(e.target.value, form.billingCycle, form.isCustomPrice)}
                    sx={catchyTextFieldSx}>
                    {plans.map((p) => (
                      <MenuItem key={p._id} value={p.planName}>
                        {p.planName} — {formatMoney(p.monthlyPrice, form.localeCurrency, form.localeLanguage)}/mo
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label="Billing Cycle" value={form.billingCycle}
                    onChange={(e) => applyPlanPrice(form.planName, e.target.value, form.isCustomPrice)}
                    sx={catchyTextFieldSx}>
                    <MenuItem value="monthly">Monthly{selectedPlan ? ` — ${formatPreview(selectedPlan.monthlyPrice)}` : ''}</MenuItem>
                    <MenuItem value="yearly">Yearly{selectedPlan ? ` — ${formatPreview(selectedPlan.yearlyPrice)}` : ''}</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}

            {form.pricingType === 'custom' && (
              <Grid item xs={12}>
                <TextField fullWidth label="Custom Plan / Service Name" value={form.customPlanName}
                  onChange={(e) => setForm((f) => ({ ...f, customPlanName: e.target.value }))}
                  sx={catchyTextFieldSx} />
              </Grid>
            )}

            {(form.pricingType === 'plan' || form.pricingType === 'custom') && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={form.isCustomPrice}
                      onChange={(e) => setForm((f) => ({ ...f, isCustomPrice: e.target.checked }))} />}
                    label="Override with custom price"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label={`Base Amount (${form.localeCurrency})`}
                    value={form.baseAmount || ''}
                    disabled={form.pricingType === 'plan' && !form.isCustomPrice}
                    onChange={(e) => setForm((f) => ({ ...f, baseAmount: Number(e.target.value) }))}
                    sx={catchyTextFieldSx} />
                </Grid>
              </>
            )}

            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography fontWeight={700} fontSize={12} color={brandCss.primaryDark}>LOCALE (INVOICE DISPLAY)</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField select fullWidth size="small" label="Language" value={form.localeLanguage}
                onChange={(e) => setForm((f) => ({ ...f, localeLanguage: e.target.value }))}
                sx={catchyTextFieldSx}>
                {languages.map((l) => <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField select fullWidth size="small" label="Currency" value={form.localeCurrency}
                onChange={(e) => setForm((f) => ({ ...f, localeCurrency: e.target.value }))}
                sx={catchyTextFieldSx}>
                {currencies.map((c) => <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField select fullWidth size="small" label="Timezone" value={form.localeTimezone}
                onChange={(e) => setForm((f) => ({ ...f, localeTimezone: e.target.value }))}
                sx={catchyTextFieldSx}>
                {timezones.map((tz) => <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField select fullWidth size="small" label="Date Format" value={form.localeDateFormat}
                onChange={(e) => setForm((f) => ({ ...f, localeDateFormat: e.target.value }))}
                sx={catchyTextFieldSx}>
                {dateFormats.map((df) => <MenuItem key={df.value} value={df.value}>{df.label}</MenuItem>)}
              </TextField>
            </Grid>
            {selectedTenant && (
              <Grid item xs={12}>
                <Typography fontSize={12} color="text.secondary">
                  Tenant defaults: {selectedTenant.language || 'en'} · {selectedTenant.currency || 'INR'} · {selectedTenant.timeZone || 'Asia/Kolkata'}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="GST Rate (%)"
                value={form.gstRate} disabled={!form.gstEnabled}
                onChange={(e) => setForm((f) => ({ ...f, gstRate: Number(e.target.value) }))}
                sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={form.gstEnabled}
                  onChange={(e) => setForm((f) => ({ ...f, gstEnabled: e.target.checked }))} />}
                label="Apply GST / Tax"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label={`Discount (${form.localeCurrency})`}
                value={form.discountAmount || ''}
                onChange={(e) => setForm((f) => ({ ...f, discountAmount: Number(e.target.value), discountPercent: 0 }))}
                sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Discount (%)"
                value={form.discountPercent || ''}
                onChange={(e) => setForm((f) => ({ ...f, discountPercent: Number(e.target.value), discountAmount: 0 }))}
                sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Mode of Payment" value={form.paymentMode}
                onChange={(e) => setForm((f) => ({ ...f, paymentMode: e.target.value }))}
                sx={catchyTextFieldSx}>
                {PAYMENT_MODES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Due Date" value={form.dueDate}
                onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Billing Period (optional)"
                placeholder="e.g. June 2026"
                value={form.billingPeriod}
                onChange={(e) => setForm((f) => ({ ...f, billingPeriod: e.target.value }))}
                sx={catchyTextFieldSx} />
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2 }}>
                <Typography fontWeight={700} fontSize={13} mb={1}>Invoice Preview</Typography>
                <Typography fontSize={13}>Subtotal: {formatPreview(preview.taxableAmount)}</Typography>
                <Typography fontSize={13}>GST: {formatPreview(preview.gstAmount)}</Typography>
                <Typography fontWeight={800} fontSize={16} color={brandCss.primaryDark}>
                  Total: {formatPreview(preview.totalAmount)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreate} disabled={!canCreate} sx={catchyPrimaryButtonSx}>
              Create Invoice
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};
